/**
 * Rhenium-specific quote PDF parser (operates on line-reconstructed text from pdfjs-dist).
 */

import { logInfo } from '../../../config_and_helpers/logger';
import { matchText, parseMoney, parseNumber } from './rheniumQuoteParserHelpers';

const LOG_CONTEXT = 'rheniumQuoteParser';

export const RHENIUM_PARSER_FORMAT_ID = 'rhenium_quote_pdf';

/**
 * @typedef {{ page?: number, y?: number, text: string }} QuotePdfLine
 */

/**
 * @param {string} text
 * @returns {boolean}
 */
export function isRheniumQuote(text) {
  return /רניום ציוד למעבדות|rhenium\.co\.il|511642449/i.test(text);
}

/**
 * @param {QuotePdfLine[]} lines
 * @param {string|null} [sourceFileName]
 * @returns {Object}
 */
export function extractRheniumQuote(lines, sourceFileName = null) {
  const text = lines.map(line => line.text).join('\n');

  if (!isRheniumQuote(text)) {
    throw new Error('This PDF does not look like a Rhenium quote');
  }

  const quote = extractQuoteHeader(text);
  const totals = extractTotals();
  const items = extractItems(lines);

  const result = {
    // Supplier/customer blocks intentionally omitted — user selects Materiah supplier in UI.
    supplier: {},
    customer: {},
    quote,
    items,
    totals,
    warnings: buildWarnings(text, items),
    meta: {
      sourceFileName,
      parserFormat: RHENIUM_PARSER_FORMAT_ID,
      parserVersion: 'rhenium-pdfjs-v1',
      rawText: text,
    },
  };

  logInfo(LOG_CONTEXT, 'extractRheniumQuote complete', {
    sourceFileName,
    quoteNumber: quote.quoteNumber,
    itemCount: items.length,
    total: totals.total,
  });

  return result;
}

/**
 * @param {string} text
 * @returns {Object}
 */
function extractQuoteHeader(text) {
  const quoteNumberRaw =
    matchText(text, /הצעת מחיר מספר\s+([A-Z0-9]+)/i, 1) ||
    matchText(text, /\*(PQ\d{2}R\d{6})\*/i, 1);

  return {
    quoteNumber: normalizeRheniumQuoteNumber(quoteNumberRaw),
    currency: 'ILS',
  };
}

/**
 * @param {string|null} value
 * @returns {string|null}
 */
export function normalizeRheniumQuoteNumber(value) {
  if (!value) return null;

  const reversed = value.match(/^(\d{6})R(\d{2})PQ$/i);

  if (reversed) {
    return `PQ${reversed[2]}R${reversed[1]}`;
  }

  return value;
}

/**
 * Merge item lines split across rows (e.g. ETA `2-3` on one line, `שבועות` on the next).
 *
 * @param {string[]} texts
 * @returns {string[]}
 */
function mergeRheniumItemLines(texts) {
  const merged = [];
  for (let i = 0; i < texts.length; i++) {
    const line = texts[i];
    const next = texts[i + 1];
    if (next?.trim() === 'שבועות' && /(\d+-\d+)\s+/.test(line)) {
      merged.push(line.replace(/(\d+-\d+)\s+/, '$1 שבועות '));
      i += 1;
    } else {
      merged.push(line);
    }
  }
  return merged;
}

/**
 * @param {QuotePdfLine[]} lines
 * @returns {Object[]}
 */
function extractItems(lines) {
  const mergedTexts = mergeRheniumItemLines(lines.map(line => line.text));
  return mergedTexts
    .filter(isLikelyRheniumItemLine)
    .map(parseRheniumItemLine)
    .filter(Boolean);
}

/**
 * @param {string} line
 * @returns {boolean}
 */
function isLikelyRheniumItemLine(line) {
  return (
    line.includes('USD') &&
    line.includes('יח') &&
    /[\d,]+\.\d{2}/.test(line) &&
    /[A-Z0-9][A-Z0-9-]{3,}/.test(line)
  );
}

/** Matches Rhenium ETA variants: `שבועות 2-3 כ-` or `שבועות 2-4-כ`. */
const RHENIUM_ETA_PATTERN = /שבועות\s+(\d+-\d+)(?:\s*כ\s*-?|-כ)/;

/** Matches inline ETA after line merge: `2-3 שבועות`. */
const RHENIUM_ETA_INLINE_PATTERN = /(\d+-\d+)\s+שבועות/;

/**
 * @param {string} line
 * @returns {string|null}
 */
function extractEtaFromLine(line) {
  const legacyMatch = line.match(RHENIUM_ETA_PATTERN);
  if (legacyMatch) {
    return `שבועות ${legacyMatch[1]} כ`;
  }
  const inlineMatch = line.match(RHENIUM_ETA_INLINE_PATTERN);
  if (inlineMatch) {
    return `${inlineMatch[1]} שבועות`;
  }
  return null;
}

/**
 * @param {string} line
 * @returns {Object|null}
 */
function parseRheniumItemLine(line) {
  const lineNumber = parseNumber(matchText(line, /\s(\d+)$/, 1));
  const catalogNumber = extractCatalogNumber(line);
  const lineTotal = parseMoney(matchText(line, /^([\d,]+\.\d{2})/, 1));

  const quantity = parseNumber(
    matchText(line, /['׳]?יח\s+(\d+(?:\.\d+)?)/, 1) ||
      matchText(line, /יח['׳]?\s+(\d+(?:\.\d+)?)/, 1)
  );

  const eta = extractEtaFromLine(line);

  const hasDiscount = /\d+(?:\.\d+)?%/.test(line);

  let unitPrice = null;
  let unitPriceBeforeDiscount = null;
  let unitPriceAfterDiscount = null;
  let discountPercent = null;

  if (hasDiscount) {
    const discountMatch = line.match(
      /^([\d,]+\.\d{2})\s+USD\s+([\d,]+\.\d{2})\s+([\d.]+)%\s+USD\s+([\d,]+\.\d{2})/
    );

    if (discountMatch) {
      unitPriceAfterDiscount = parseMoney(discountMatch[2]);
      discountPercent = parseNumber(discountMatch[3]);
      unitPriceBeforeDiscount = parseMoney(discountMatch[4]);
      unitPrice = unitPriceBeforeDiscount;
    }
  } else {
    const regularMatch = line.match(/^([\d,]+\.\d{2})\s+USD\s+([\d,]+\.\d{2})/);

    if (regularMatch) {
      unitPrice = parseMoney(regularMatch[2]);
    }
  }

  if (lineTotal == null && catalogNumber == null) {
    return null;
  }

  return {
    lineNumber,
    catalogNumber,
    description: extractDescription(line, catalogNumber),
    eta,
    quantity,
    unit: "יח'",
    unitPrice,
    unitPriceBeforeDiscount,
    discountPercent,
    unitPriceAfterDiscount,
    unitPriceCurrency: 'USD',
    lineTotal,
    lineTotalCurrency: 'ILS',
    rawLine: line,
  };
}

/**
 * @param {string} line
 * @returns {string|null}
 */
function extractCatalogNumber(line) {
  const suffixMatch = line.match(/\s(\d{5,})\s+(\d{1,3})\s*$/);
  if (suffixMatch) {
    return suffixMatch[1];
  }

  const candidates = line.match(/\b[A-Z]*\d+[A-Z0-9-]*\b/g) || [];

  const filtered = candidates.filter(value => {
    if (/^\d+$/.test(value)) return false;
    if (/^\d+\.\d+$/.test(value)) return false;
    if (/^\d{2,3}$/.test(value)) return false;
    return true;
  });

  return filtered.length > 0 ? filtered[filtered.length - 1] : null;
}

/**
 * @param {string} line
 * @param {string|null} catalogNumber
 * @returns {string|null}
 */
function extractDescription(line, catalogNumber) {
  if (!catalogNumber) return null;

  const beforeCatalog = line.split(catalogNumber)[0];

  const cleaned = beforeCatalog
    .replace(/^[\d,]+\.\d{2}\s+USD\s+/, '')
    .replace(/^[\d,]+\.\d{2}\s+/, '')
    .replace(/USD\s+[\d,]+\.\d{2}/g, '')
    .replace(/[\d.]+%/g, '')
    .replace(/['׳]?יח\s+\d+(?:\.\d+)?/, '')
    .replace(/יח['׳]?\s+\d+(?:\.\d+)?/, '')
    .replace(RHENIUM_ETA_PATTERN, '')
    .replace(RHENIUM_ETA_INLINE_PATTERN, '')
    .replace(/\s\d{5,}-\d+(?:-\d+)?\s*$/, '')
    .replace(/^\s*-\s*/, '')
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned || null;
}

/**
 * @param {string} text
 * @returns {Object}
 */
function extractTotals() {
  return { currency: 'ILS' };
}

/**
 * @param {string} text
 * @param {Object[]} items
 * @returns {Array<{ field: string, message: string, severity: string }>}
 */
function buildWarnings(text, items) {
  const warnings = [];

  if (!/PQ\d{2}R\d{6}|הצעת מחיר מספר/.test(text)) {
    warnings.push({
      field: 'quote.quoteNumber',
      message: 'Quote number could not be confidently detected.',
      severity: 'warning',
    });
  }

  if (!items.length) {
    warnings.push({
      field: 'items',
      message: 'No line items detected; complete the quote manually.',
      severity: 'warning',
    });
  }

  return warnings;
}
